import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Typography, Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import { RESET_PASSWORD, REGISTER } from '../../../../routes';
import { STATUS } from '../../../../redux/constants';
import history from '../../../../redux/history';
import Cookies from 'cookies-js';
import logo from '../../../../assets/sp_new_logo_reversed.svg';
import styles from './login.module.sass';
import { useDispatch, useSelector } from 'react-redux';
import { login, loginWith2FA } from '../../../../redux/actions/auth';
import { SPLinks } from '../../../../shared-components/AuthLayout';
import { changeTitle } from '../../../../redux/actions/app';
import Panel from '../../../../shared-components/Panel';
import { formatEmail } from '../../../../redux/utils/email';

function PinStepForm() {
  const dispatch = useDispatch();
  const handlePinSubmit = (data) => {
    dispatch(loginWith2FA(data));
  };

  return (
    <Form
      className="login-form"
      initialValues={{
        remember: true,
      }}
      onFinish={handlePinSubmit}
    >
      <Form.Item
        name="pin"
        rules={[
          {
            required: true,
            message: 'Pin Code is required',
          },
        ]}
      >
        <Input autoFocus placeholder="Pin Code" />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" className="login-form-button">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

export default function Login() {
  const [showPinForm, setShowPinForm] = useState(false);
  const loggedInUser = useSelector((state) => state.user);
  const loginStatus = useSelector((state) => state.auth.loginStatus);
  const is2faEnabled = useSelector((state) => state.auth.is2faEnabled);
  const loginError = useSelector((state) => state.auth.loginError);
  const redirectTo = useSelector((state) => state.auth.redirectTo);
  const correct_env = useSelector((state) => state.auth.env);
  const dispatch = useDispatch();
  const formRef = React.createRef();
  const { Text, Title } = Typography;

  useEffect(() => {
    document.title = 'Login - Silent Push';
    dispatch(changeTitle('Login'));
  }, []);

  useEffect(() => {
    if (loginStatus === STATUS.LOADED) {
      const tokenCookie = Cookies.get('persist:token');
      if (loggedInUser && tokenCookie) {
        const tokenFromCookie = JSON.parse(tokenCookie)['token'];
        const isTokenPresentInCookie =
          tokenFromCookie && tokenFromCookie !== '""';
        if (isTokenPresentInCookie && !redirectTo) {
          history.push('/explore');
          return () => {};
        }
      }
      if (is2faEnabled) {
        setShowPinForm(true);
      }
    }
  }, [loggedInUser, loginStatus, is2faEnabled]);

  const handleLogin = (data) => {
    const trimmedObject = {
      email: data?.email?.trim(),
      password: data?.password?.trim(),
    };
    dispatch(login(trimmedObject));
  };

  const handleChange = (values) => {
    if (values?.email) {
      formRef.current.setFieldsValue({
        email: formatEmail(values.email),
      });
    }
  };

  return (
    <Row className={styles.login} gutter={24} align="middle" justify="center">
      <Col xs={24} md={13} lg={10} xl={9} xxl={7}>
        <Panel
          bgcolor={
            process.env.REACT_APP_FRONTEND_NODE_ENV === 'explore' ||
            process.env.REACT_APP_FRONTEND_NODE_ENV === 'dev-explore'
              ? '#255967'
              : '#1C3138'
          }
          className={styles.loginPanel}
        >
          {showPinForm && <PinStepForm />}
          {!showPinForm && (
            <>
              <img
                alt={'silentpush logo'}
                title="SilentPush Logo"
                src={logo}
                width={250}
              />
              <Title level={2}>
                Login to your
                {(process.env.REACT_APP_FRONTEND_NODE_ENV === 'explore' ||
                  process.env.REACT_APP_FRONTEND_NODE_ENV === 'dev-explore') &&
                  ` Community Edition`}{' '}
                account
              </Title>
              <Text className={styles.helpText}>
                To login, use your credentials with which your account was
                created.
              </Text>

              {loginError && !redirectTo && (
                <Text className={styles.error_text}>
                  Incorrect email or password entered.
                </Text>
              )}

              {loginStatus === STATUS.ERROR && redirectTo && (
                <Text className={styles.error_text}>
                  You are trying to log in to incorrect environment. Click on
                  the <a href={redirectTo}>link</a> to log in to{' '}
                  {correct_env === 'Explore' ? 'Community' : correct_env}{' '}
                  application
                </Text>
              )}

              <Form
                ref={formRef}
                onFinish={handleLogin}
                onValuesChange={handleChange}
                layout={'vertical'}
              >
                <Form.Item
                  label="Email (required)"
                  name="email"
                  rules={[
                    { required: true, message: 'Please input your email!' },
                    {
                      type: 'email',
                      message: 'Please input a valid email!',
                    },
                  ]}
                >
                  <Input placeholder={'Enter your email address'} />
                </Form.Item>

                <Form.Item
                  className={styles.password_input}
                  label="Password (required)"
                  name="password"
                  rules={[
                    { required: true, message: 'Please input your password!' },
                  ]}
                >
                  <Input.Password placeholder={'Enter your password'} />
                </Form.Item>
                <Link
                  className={styles.forgot_password_link}
                  to={RESET_PASSWORD}
                >
                  Forgot Password
                </Link>
                {(process.env.REACT_APP_FRONTEND_NODE_ENV === 'explore' ||
                  process.env.REACT_APP_FRONTEND_NODE_ENV ===
                    'dev-explore') && (
                  <Link
                    className={styles.register_link}
                    // style={{ marginLeft: '8px' }}
                    // className="forgot-password"
                    to={REGISTER}
                  >
                    Register
                  </Link>
                )}
                <Text className={styles.info}>
                  By clicking on the login button you are accepting our{' '}
                  <a
                    target="_blank"
                    href="https://www.silentpush.com/privacy-policy"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    target="_blank"
                    href="https://www.silentpush.com/terms-of-use"
                  >
                    Terms of services
                  </a>
                </Text>
                <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                  <Button type="primary" htmlType="submit" size="large">
                    Login
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}
        </Panel>

        <SPLinks />
      </Col>
    </Row>
  );
}
